import type { WorkflowFieldDef } from './config';

/**
 * Starting values that belong to the FIELD, not to a workflow.
 *
 * Applied by field name, for the same reason `fieldFormat.ts` validates by field name: workflows are
 * runtime data that site admins edit, so a value put in the shipped defaults never reaches a configuration
 * already saved — and every SWFT workflow that has these inputs wants the same three codes. Keeping them
 * in one place here is also what stops the live-capture and card-conversion forms drifting apart, which is
 * exactly what happened when they were set per workflow: the conversion form had them and live capture
 * did not.
 *
 * SON and SOI identify the SUBMITTING OFFICE, so these are a starting point and not a policy. A workflow
 * that sets its own `defaultValue` still wins, which is the escape hatch for a customer submitting under
 * their own codes.
 *
 * IPAC was `97008801` until October 2026; `DSS-IND` is the current code.
 */
export const FIELD_DEFAULTS: Readonly<Record<string, string>> = {
  son: '346W',
  soi: 'DD03',
  ipac: 'DSS-IND',
};

/**
 * Fields whose last used value becomes that customer's own default.
 *
 * MUST match `REMEMBERED_FIELDS` in `services/lib/domain/fieldPrefs.ts`, which refuses anything not on
 * its own copy of the list — the browser decides what to send and the server decides what to keep.
 *
 * The CAGE codes are here but have no shipped default: there is no sensible value to invent for one, and
 * every customer's is different. Remembering is the whole benefit for those two.
 */
export const REMEMBERED_FIELDS = ['son', 'soi', 'ipac', 'cageCode', 'parentCageCode'] as const;

/** What to send back for remembering, from a submitted form. */
export function rememberable(values: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  // Every remembered name is sent, including the blanks: an emptied field has to be able to clear.
  for (const name of REMEMBERED_FIELDS) out[name] = (values[name] ?? '').trim();
  return out;
}

/**
 * Seed a demographic map from the workflow's default values.
 *
 * Fills BLANKS only. A default is a convenience for a field that has to be filled anyway — a fixed reason
 * fingerprinted, an agency that is always the same — and it must never win over a value that already exists:
 * a reopened draft, an imported file, or something the operator typed. Overwriting any of those would look
 * like the form editing itself.
 *
 * Returns the SAME object when there is nothing to add, so a caller can use it in a setState updater without
 * causing a render on every pass.
 */
export function withDefaults(
  fields: WorkflowFieldDef[],
  values: Record<string, string>,
  /**
   * What this customer last submitted. Beats both the workflow's default and the shipped one: a customer
   * who typed their own submitting-office codes has said, as plainly as the form allows, what theirs are.
   */
  remembered: Record<string, string> = {},
): Record<string, string> {
  let next = values;
  for (const f of fields) {
    if (!f.name) continue;
    // Their own value, then the workflow's (a site admin set it deliberately), then the shipped one.
    const d = remembered[f.name]?.trim() || f.defaultValue || FIELD_DEFAULTS[f.name];
    if (!d) continue;
    if ((values[f.name] ?? '').trim()) continue;
    if (next === values) next = { ...values };
    next[f.name] = d;
  }
  return next;
}
